import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { LOGO_URL, HERO_BG, ABOUT_IMG, TEXTURE_IMG } from "@/lib/constants";
import { ArrowRight, Phone, MapPin, Instagram, Facebook, Menu, X } from "lucide-react";

export default function Landing() {
  const [settings, setSettings] = useState(null);
  const [qualities, setQualities] = useState([]);
  const [activeQuality, setActiveQuality] = useState(null);
  const [qualityProducts, setQualityProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    document.title = "Evser Halı – Toptan Halı Satışı";
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/public/qualities").then(({ data }) => setQualities(data)).catch(() => {});
  }, []);

  const openQuality = async (q) => {
    setActiveQuality(q);
    setLoadingProducts(true);
    try {
      const { data } = await api.get(`/public/qualities/${q.id}/products`);
      setQualityProducts(data);
    } catch {
      setQualityProducts([]);
    } finally {
      setLoadingProducts(false);
    }
    // smooth scroll
    setTimeout(() => {
      const el = document.getElementById("kalite-urunleri");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const closeQuality = () => {
    setActiveQuality(null);
    setQualityProducts([]);
    setTimeout(() => {
      const el = document.getElementById("koleksiyonlar");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const s = settings || {};

  return (
    <div className="bg-[#f7f5f0] text-[#1a1a1a]">
      {/* Header */}
      <Header />

      {/* Hero */}
      <section id="anasayfa" className="relative min-h-screen flex items-center overflow-hidden pt-24 md:pt-28 pb-16 md:pb-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            filter: "saturate(0.7) brightness(0.92)",
          }}
        />
        {/* Bottom-fade so the section blends into the cream page */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#f7f5f0]/30 to-[#f7f5f0]" />
        {/* Left-side dark vignette to make hero text crisp & legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/65 via-[#1a1a1a]/25 to-transparent lg:via-[#1a1a1a]/10" />
        <div className="grain" />

        <div className="relative w-full max-w-7xl mx-auto px-5 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7"
          >
            <span className="label-tag text-[#e8c896]">Toptan – B2B Halı Tedariği</span>
            <h1 className="font-serif text-[2.75rem] sm:text-5xl md:text-7xl lg:text-8xl leading-[1.05] md:leading-none mt-5 md:mt-6 tracking-tight break-words text-[#f7f5f0]" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.35)" }}>
              {s.hero_title || "Evser Halı"}
              <br />
              <em className="not-italic font-normal text-[#e8c896]" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55)" }}>
                {s.hero_subtitle || "Toptan Halı Satışı"}
              </em>
            </h1>
            <p className="mt-6 md:mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#ece8df]" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.45)" }}>
              {s.hero_description ||
                "Mağaza ve bayilere özel premium halı koleksiyonları. Üretici güvencesi, geniş ürün yelpazesi."}
            </p>
            <div className="mt-8 md:mt-12 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4" data-testid="hero-cta-group">
              <Link to="/bayi-girisi" className="btn-luxe justify-center sm:justify-start" data-testid="hero-bayi-girisi-btn">
                Bayi Girişi
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a href="#koleksiyonlar" className="btn-luxe-outline justify-center sm:justify-start" data-testid="hero-koleksiyonlar-btn">
                Kaliteleri İncele
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <HeroSlider />
          </motion.div>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-[#6b655c] text-xs tracking-[0.3em] uppercase">
          <span>Keşfet</span>
          <div className="w-px h-12 bg-[#c8a97e] mt-2 animate-pulse" />
        </div>
      </section>

      {/* Marquee ribbon */}
      <div className="bg-[#1a1a1a] text-[#f7f5f0] py-6 overflow-hidden border-y border-[#333]">
        <div className="flex gap-12 marquee-track whitespace-nowrap">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <span key={i} className="font-serif text-2xl md:text-3xl px-6 flex items-center gap-10">
                {[
                  "Güvenin ve kalitenin adresi.",
                  "Türkiye'nin yükselen halı markası.",
                  "Bayilerimizin gücüyle her yerdeyiz.",
                  "Toptan halıda güçlü çözüm ortağınız.",
                  "Modern çizgiler, güçlü kalite.",
                ].map((slogan) => (
                  <span key={slogan} className="flex items-center gap-10">
                    <span>{slogan}</span>
                    <span className="text-[#c8a97e]">✦</span>
                    <span className="text-[#c8a97e] tracking-[0.2em] text-lg md:text-xl font-medium uppercase">Evser Halı</span>
                    <span className="text-[#c8a97e]">✦</span>
                  </span>
                ))}
              </span>
            ))}
        </div>
      </div>

      {/* Qualities (Kaliteler) */}
      <section id="koleksiyonlar" className="py-20 md:py-32 px-5 md:px-12 lg:px-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-12 md:mb-16">
          <div className="md:col-span-4">
            <span className="label-tag">{s.collection_section_subtitle || "Kaliteler"}</span>
            <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
              {s.collection_section_title || "Kaliteler"}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-[#6b655c] text-base md:text-lg leading-relaxed">
              Halılarımız kaliteye göre kategorize edilir; her kalite altında onlarca
              farklı desen bulunur. İncelemek istediğiniz kaliteyi seçin, desenleri görüntüleyin.
              Sipariş için bayi girişi yapmanız yeterli.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="quality-grid">
          {qualities.length === 0 && (
            <p className="col-span-full text-[#6b655c] py-12 text-center">Henüz kalite eklenmedi.</p>
          )}
          {qualities.map((q, idx) => (
            <motion.button
              key={q.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.05 }}
              onClick={() => openQuality(q)}
              className={`group text-left bg-white border transition-all ${
                activeQuality?.id === q.id ? "border-[#1a1a1a] shadow-lg" : "border-[#eae5dc] hover:border-[#c8a97e]"
              }`}
              data-testid={`quality-card-${q.id}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#eae5dc] relative">
                {q.image_path ? (
                  <img
                    src={q.image_path}
                    alt={q.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#6b655c]">Görsel yok</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-[#f7f5f0]">
                  <div className="text-[0.65rem] uppercase tracking-[0.25em] text-[#c8a97e] mb-2">
                    {q.product_count} desen
                  </div>
                  <h3 className="font-serif text-3xl leading-tight">{q.name}</h3>
                  <p className="text-sm text-[#d4cfc4] mt-2 line-clamp-2">{q.short_description}</p>
                </div>
              </div>
              <div className="p-4 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-[#c8a97e]">Desenleri Gör</span>
                <ArrowRight size={16} className="text-[#1a1a1a] transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </div>
            </motion.button>
          ))}
        </div>

        {/* Expanded quality products */}
        {activeQuality && (
          <motion.div
            id="kalite-urunleri"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-16 md:mt-24 border-t border-[#eae5dc] pt-12 md:pt-16"
            data-testid="quality-products-section"
          >
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-12">
              <div>
                <button
                  onClick={closeQuality}
                  className="text-xs uppercase tracking-[0.25em] text-[#c8a97e] hover:underline flex items-center gap-2 mb-3"
                  data-testid="quality-back-btn"
                >
                  ← Tüm Kaliteler
                </button>
                <span className="label-tag">Kalite</span>
                <h3 className="font-serif text-3xl md:text-5xl mt-2 leading-tight">{activeQuality.name}</h3>
                {activeQuality.short_description && (
                  <p className="text-[#6b655c] mt-3 max-w-xl">{activeQuality.short_description}</p>
                )}
              </div>
              <Link to="/bayi-girisi" className="btn-luxe text-xs" data-testid="quality-bayi-cta-btn">
                Sipariş İçin Bayi Girişi
                <ArrowRight size={14} />
              </Link>
            </div>

            {loadingProducts && <p className="text-[#6b655c] text-center py-12">Yükleniyor…</p>}

            {!loadingProducts && qualityProducts.length === 0 && (
              <p className="text-[#6b655c] text-center py-12">Bu kalitede henüz ürün yok.</p>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {qualityProducts.map((p, idx) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.04 }}
                  className="group"
                  data-testid={`quality-product-${p.id}`}
                >
                  <div className="aspect-[4/5] bg-[#eae5dc] overflow-hidden relative">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#6b655c] text-xs">Görsel</div>
                    )}
                    <div className="absolute inset-0 bg-[#1a1a1a]/0 group-hover:bg-[#1a1a1a]/30 transition-colors flex items-center justify-center">
                      <Link
                        to="/bayi-girisi"
                        className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#f7f5f0] text-[#1a1a1a] px-4 py-2 text-[0.7rem] uppercase tracking-[0.2em]"
                      >
                        Sipariş Vermek İçin
                      </Link>
                    </div>
                  </div>
                  <div className="mt-3">
                    <h4 className="font-serif text-base md:text-lg leading-tight">{p.name}</h4>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        )}

        {!activeQuality && (
          <div className="mt-12 md:mt-16 text-center">
            <Link to="/bayi-girisi" className="btn-luxe-outline text-xs" data-testid="showcase-cta-btn">
              Tüm Ürünler İçin Bayi Girişi
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </section>

      {/* About */}
      <section id="hakkimizda" className="py-24 md:py-32 bg-[#1a1a1a] text-[#f7f5f0] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={TEXTURE_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              {s.about_title || "Hakkımızda"}
            </h2>
            <span className="gold-divider mt-6" />
          </div>
          <div className="md:col-span-7">
            <p className="text-base md:text-lg leading-loose text-[#d4cfc4] whitespace-pre-line">
              {s.about_text ||
                "Evser Halı, mağaza ve bayilere kaliteli halı tedariki sunmaktadır."}
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <div className="font-serif text-4xl text-[#c8a97e]">10+</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a09a8e] mt-2">Koleksiyon</div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#c8a97e]">100+</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a09a8e] mt-2">Bayi Ağı</div>
              </div>
              <div>
                <div className="font-serif text-4xl text-[#c8a97e]">∞</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a09a8e] mt-2">İhtimal</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="iletisim" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <span className="label-tag">Bize Ulaşın</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              {s.contact_title || "İletişim"}
            </h2>
            <p className="text-[#6b655c] mt-6 leading-relaxed">
              Bayilik başvuruları, ürün bilgileri ve toptan tedarik hakkında bizimle iletişime geçin.
            </p>
            <div className="mt-12 space-y-6">
              <a href={`tel:${(s.contact_phone || "").replace(/\s/g, "")}`} className="flex items-start gap-4 group">
                <Phone size={18} className="text-[#c8a97e] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#6b655c]">Telefon</div>
                  <div className="text-lg mt-1 group-hover:text-[#c8a97e] transition-colors">{s.contact_phone}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[#c8a97e] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#6b655c]">Adres</div>
                  <div className="text-lg mt-1">{s.contact_address}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="aspect-[4/3] bg-[#1a1a1a] flex items-center justify-center text-[#f7f5f0] relative overflow-hidden">
              <img src={TEXTURE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <div className="relative text-center px-8">
                <div className="label-tag" style={{ color: "#c8a97e" }}>Bayilik Başvurusu</div>
                <p className="font-serif text-3xl mt-4 leading-tight">
                  Mağaza ve toptancı bayilerimize<br />özel iş ortaklığı.
                </p>
                <Link to="/bayi-girisi" className="mt-8 btn-luxe inline-flex" data-testid="contact-cta-btn">
                  Bayi Girişi
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0e0e0e] text-[#a09a8e] py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img src={LOGO_URL} alt="Evser Halı" className="h-16 w-auto bg-white p-2" />
            <p className="mt-6 max-w-sm leading-relaxed text-sm">
              {s.site_description || "Toptan halı satışı ve bayi tedariki için profesyonel çözüm ortağınız."}
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="label-tag">Menü</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="#anasayfa" className="hover:text-[#c8a97e]">Anasayfa</a></li>
              <li><a href="#koleksiyonlar" className="hover:text-[#c8a97e]">Kaliteler</a></li>
              <li><a href="#hakkimizda" className="hover:text-[#c8a97e]">Hakkımızda</a></li>
              <li><a href="#iletisim" className="hover:text-[#c8a97e]">İletişim</a></li>
              <li><Link to="/bayi-girisi" className="hover:text-[#c8a97e]">Bayi Girişi</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="label-tag">Takip Edin</div>
            <div className="mt-4 flex gap-3">
              {s.social_instagram && (
                <a href={s.social_instagram} target="_blank" rel="noreferrer" className="p-3 border border-[#333] hover:border-[#c8a97e] hover:text-[#c8a97e]"><Instagram size={16} strokeWidth={1.5}/></a>
              )}
              {s.social_facebook && (
                <a href={s.social_facebook} target="_blank" rel="noreferrer" className="p-3 border border-[#333] hover:border-[#c8a97e] hover:text-[#c8a97e]"><Facebook size={16} strokeWidth={1.5}/></a>
              )}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-[#222] flex flex-col md:flex-row justify-between gap-3 text-xs">
          <span>© {new Date().getFullYear()} Evser Halı. Tüm hakları saklıdır.</span>
          <span>Toptan B2B Bayi Sipariş Sistemi</span>
        </div>
        <div className="max-w-7xl mx-auto mt-3 text-xs text-[#6b655c]">
          Bu web sitesi ve B2B yapısı{" "}
          <a
            href="https://dijitalroket.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#c8a97e] hover:text-[#e8c896] underline-offset-4 hover:underline transition-colors"
            data-testid="footer-credit-link"
          >
            Dijital Roket
          </a>{" "}
          tarafından geliştirilmiştir.
        </div>
      </footer>
    </div>
  );
}

const HERO_SLIDES = [
  "https://customer-assets.emergentagent.com/job_b141beba-8f83-4f0c-b2cf-e46a161f89fe/artifacts/xc6v4hp9_ChatGPT%20Image%2013%20May%202026%2010_25_50.png",
  "https://customer-assets.emergentagent.com/job_b141beba-8f83-4f0c-b2cf-e46a161f89fe/artifacts/sn3nolby_ChatGPT%20Image%2013%20May%202026%2010_28_13.png",
  "https://customer-assets.emergentagent.com/job_b141beba-8f83-4f0c-b2cf-e46a161f89fe/artifacts/v3a9qevs_ChatGPT%20Image%2013%20May%202026%2010_44_12.png",
];

function HeroSlider() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative aspect-[4/5] overflow-hidden bg-[#1a1a1a]" data-testid="hero-slider">
      {HERO_SLIDES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden={i !== idx}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slayt ${i + 1}`}
            className={`h-1.5 transition-all duration-500 ${i === idx ? "w-8 bg-[#c8a97e]" : "w-3 bg-white/60 hover:bg-white"}`}
            data-testid={`hero-slider-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const links = [
    ["Anasayfa", "#anasayfa"],
    ["Kaliteler", "#koleksiyonlar"],
    ["Hakkımızda", "#hakkimizda"],
    ["İletişim", "#iletisim"],
  ];

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-[#f7f5f0]/95 backdrop-blur-md border-b border-[#eae5dc] py-3 text-[#1a1a1a]"
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-24 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 md:gap-3 min-w-0" data-testid="public-logo-link" onClick={() => setOpen(false)}>
          <img src={LOGO_URL} alt="Evser Halı" className="h-10 md:h-12 w-auto flex-shrink-0" />
          <div className="hidden sm:block leading-tight min-w-0">
            <div className="font-serif text-lg md:text-xl truncate">Evser Halı</div>
            <div className="text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.25em] text-[#6b655c]">Toptan – B2B</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 lg:gap-10 text-sm tracking-wide">
          {links.map(([label, hash]) => (
            <a
              key={hash}
              href={hash}
              className="text-[#1a1a1a] hover:text-[#c8a97e] transition-colors"
              data-testid={`nav-${hash.replace("#", "")}`}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/bayi-girisi"
            className="btn-luxe text-[0.65rem] md:text-xs px-3 md:px-5 py-2.5 md:py-3"
            data-testid="header-bayi-girisi-btn"
          >
            Bayi Girişi
          </Link>
          <button
            className="md:hidden p-2 -mr-2 hover:bg-[#eae5dc]/40 transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü"
            data-testid="public-mobile-menu-btn"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-x-0 top-[60px] bottom-0 bg-[#f7f5f0] border-t border-[#eae5dc] transition-all duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="public-mobile-menu"
      >
        <nav className="flex flex-col px-5 py-6">
          {links.map(([label, hash]) => (
            <a
              key={hash}
              href={hash}
              onClick={() => setOpen(false)}
              className="font-serif text-2xl py-4 border-b border-[#eae5dc] hover:text-[#c8a97e] transition-colors"
              data-testid={`mobile-nav-${hash.replace("#", "")}`}
            >
              {label}
            </a>
          ))}
          <Link
            to="/bayi-girisi"
            onClick={() => setOpen(false)}
            className="btn-luxe justify-center mt-8"
            data-testid="mobile-nav-bayi-girisi"
          >
            Bayi Girişi
            <ArrowRight size={16} />
          </Link>
        </nav>
      </div>
    </header>
  );
}
